import { mapGetters, mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import tabs from '@/components/generics/tabs/tabs.vue';
import orderBookOffersSushi from '@/components/purchase/sushi/order-book-sushi/order-book-offers-sushi/order-book-offers-sushi.vue';
import orderBookAllOffersSushi from '@/components/purchase/sushi/order-book-sushi/order-book-all-offers-sushi/order-book-all-offers-sushi.vue';
import orderBookPricesSushi from '@/components/purchase/sushi/order-book-sushi/order-book-prices-sushi/order-book-prices-sushi.vue';
import orderBookSharesSushi from '@/components/purchase/sushi/order-book-sushi/order-book-shares-sushi/order-book-shares-sushi.vue';
import loaderMixin from '@/mixins/loader';
import log from '@/logger';
import ls from '@/helpers/localStorage';
import { isMarketSuspended, isMarketExpired } from '@/utils/helpers';
import { waitForFirebaseInitialized } from '@/utils/mutex';
import store from '@/store';


const ORDER_BOOK_LS_KEY = 'order-book-contract-id';
const TAB_PRICES = 'prices';
const TAB_MY_SHARES = 'my-shares';
const TAB_BUY_OFFERS = 'buy-offers';
const TAB_SELL_OFFERS = 'sell-offers';
const TAB_ALL_OFFERS = 'all-offers';

const ORDER_BOOK_DEBOUNCE_INTERVAL = 500;
const debounceRefreshOrderBook = debounce((instance) => {
  if (!store.getters['user/isLoggedIn']) {
    log.info('[ORDER BOOK]: api - load contracts and shares (not logged in)');
    instance.isLoading = false;
    return;
  }
  log.info('[ORDER BOOK]: api - load contracts and shares (logged in)');
  const currentContractId = instance.contract.contractId;
  // const localStorageContractId = ls.get(ORDER_BOOK_LS_KEY, null);
  //
  // if (!(localStorageContractId && localStorageContractId === currentContractId)) {
  //   log.info('[ORDER BOOK]: api - clear contracts and shares before load');
  //   instance.getContractShares(null);
  //   instance.getContractOffers(null);
  // }

  // TODO: think about refactoring
  const promises = [];

  if (
    instance.contract.userOpenOrdersBuyQuantity > 0 ||
    instance.contract.userOpenOrdersSellQuantity > 0) {
    log.info('[ORDER BOOK] will fetch offers');
    promises.push(instance.getContractOffers(instance.contract.contractId));
  } else {
    log.info('[ORDER BOOK] should clear offers');
    instance.getContractOffers(null);
  }

  if (instance.contract.userQuantity > 0) {
    log.info('[ORDER BOOK] will fetch shares');
    promises.push(instance.getContractShares(instance.contract.contractId));
  } else {
    log.info('[ORDER BOOK] should clear shares');
    instance.getContractShares(null);
  }

  log.info(`[ORDER BOOK]: calling api - ${promises.length} promises.`);
  if (promises.length > 0) {
    Promise.all(promises)
      .then(() => {
        ls.set(ORDER_BOOK_LS_KEY, currentContractId);
        instance.initialised = true;
        instance.isLoading = false;
      })
      .catch(() => {
        ls.set(ORDER_BOOK_LS_KEY, null);
        instance.initialised = true;
        instance.isLoading = false;
      });
  } else {
    instance.isLoading = false;
    ls.set(ORDER_BOOK_LS_KEY, currentContractId);
    instance.initialised = true;
  }
}, ORDER_BOOK_DEBOUNCE_INTERVAL);

const debounceGetOrderBook = debounce((instance) => {
  log.info('[ORDER BOOK]: api - load order book');
  instance.getOrderBook(instance.contract.contractId);
}, ORDER_BOOK_DEBOUNCE_INTERVAL);

const isPricesDisabled = market => isMarketExpired(market) || isMarketSuspended(market);


export default {
  mixins: [
    loaderMixin,
  ],
  components: {
    tabs,
    orderBookOffersSushi,
    orderBookPricesSushi,
    orderBookSharesSushi,
    orderBookAllOffersSushi,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
  },
  data() {
    const isInitialised = !isPricesDisabled(this.market);
    return {
      initialised: isInitialised,
      activeTab: TAB_PRICES
    };
  },
  watch: {
    initialised(value) {
      if (!value) return;
      if (this.tabs.find(tab => tab.name === this.activeTab) === undefined) {
        if (this.tabs.find(tab => tab.name === TAB_PRICES) !== undefined) {
          this.tabChange(TAB_PRICES);
        } else if (this.tabs.length > 0) {
          this.tabChange(this.tabs[0].name);
        }
      }
      this.$emit('initialised');
    },
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapState('trade', ['orderBook', 'contractShares', 'contractOffers']),
    ...mapGetters('user', ['isLoggedIn']),
    buyOffers() {
      if (this.contract.userOpenOrdersBuyQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType <= 1) : [];
    },
    sellOffers() {
      if (this.contract.userOpenOrdersSellQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType > 1) : [];
    },
    allOffers() {
      return [...this.buyOffers, ...this.sellOffers]
    },
    tabs() {
      const tabsAvailable = [];
      if (!isPricesDisabled(this.market)) {
        tabsAvailable.push({ name: TAB_PRICES, title: 'Prices' });
      }
      tabsAvailable.push({ name: TAB_MY_SHARES, title: 'My Holdings' });
      // if (this.buyOffers && this.buyOffers.length) {
      //   tabsAvailable.push({ name: TAB_BUY_OFFERS, title: 'Buy Offers' });
      // }
      // if (this.sellOffers && this.sellOffers.length) {
      //   tabsAvailable.push({ name: TAB_SELL_OFFERS, title: 'Sell Offers' });
      // }
      tabsAvailable.push({ name: TAB_ALL_OFFERS, title: 'Orders' });
      return tabsAvailable;
    },
  },
  methods: {
    ...mapActions('market', ['getMarketContracts']),
    ...mapActions('trade', [
      'getContractShares', 'getContractOffers',
      'updateOrderBookPeriodically', 'clearOrderBookUpdates',
      'getOrderBook',
    ]),
    tabChange(name) {
      this.activeTab = name;
    },
    tabVisible(name) {
      const names = this.tabs.map(i => i.name);
      return !!this.orderBook && names.includes(name);
    },
    refreshOrderBook() {
      debounceRefreshOrderBook(this);
    },
    offerCanceled() {
      this.isLoading = true;
      this.refreshOrderBook();
      this.getMarketContracts(this.market.marketId);
    },
  },
  created() {
    log.info('[ORDER BOOK]: reinit');
  },
  mounted() {
    this.isLoading = true;
    waitForFirebaseInitialized()
      .then(() => {
        if (this.isLoggedIn) {
          debounceGetOrderBook(this);
        }
        this.updateOrderBookPeriodically(this.contract.contractId);
        this.refreshOrderBook();
      });
  },
  beforeDestroy() {
    this.clearOrderBookUpdates(this.contract.contractId);
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-sushi.js